import React, { useEffect, useState } from "react";
import { DollarSign, Download, ShoppingBag, TrendingUp } from "lucide-react";

import { Button } from "@/components/ui/button";

import FiltroPeriodo from "../components/FiltroPeriodo";
import { CardResumo, Vazio } from "../components/Estados";
import { Esmaecer, EsqueletoProdutos } from "../components/Esqueletos";
import { buscarDetalhado } from "../lib/api";
import { exportarExcel } from "../lib/excel";
import { textoTotais, totaisPorMedida } from "../../../lib/unidadeVenda";
import { formatCurrency } from "../lib/formato";
import { rotuloArquivo } from "../lib/periodo";
import { useDados, usePeriodo } from "../lib/usePeriodo";
import TabelaProdutos, { ordenar } from "./TabelaProdutos";
import Top10Produtos from "./Top10Produtos";

const COLUNAS_EXCEL = [
  { header: "#", value: (_, i) => i + 1 },
  { header: "Produto", value: (r) => r.nome },
  { header: "Quantidade", value: (r) => r.quantidade },
  { header: "Unidade", value: (r) => r.unidade || "un" },
  { header: "Faturamento (R$)", value: (r) => r.valor_total },
  { header: "CMV Total (R$)", value: (r) => r.cmv_total || 0 },
  { header: "Lucro (R$)", value: (r) => r.lucro || 0 },
  { header: "Margem (%)", value: (r) => r.margem_pct || 0 },
  ...["Mesas", "Comandas", "Balcão", "Totem", "Delivery", "Cardápio"].map((m) => ({ header: `${m} (qtd)`, value: (r) => r.por_modulo?.[m]?.qtd || 0 })),
];

const tomMargem = (m) => (m >= 50 ? "bg-emerald-500/5 border-emerald-500/30" : m >= 30 ? "bg-green-500/5 border-green-500/30"
  : m >= 15 ? "bg-amber-500/5 border-amber-500/30" : "bg-red-500/5 border-red-500/30");
const textoMargem = (m) => (m >= 30 ? "text-emerald-600 dark:text-emerald-400" : m >= 15 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400");

function Kpis({ produtos, servicos }) {
  // Quilo e litro somam à parte: "40 un · 12,350 kg" (30/09/2026).
  const qtd = textoTotais(totaisPorMedida(produtos, "quantidade"));
  const fat = produtos.reduce((s, p) => s + (p.valor_total || 0), 0);
  const cmv = produtos.reduce((s, p) => s + (p.cmv_total || 0), 0);
  const lucro = fat - cmv;
  const margem = fat > 0 ? (lucro / fat) * 100 : 0;
  const linha = (rotulo, valor, cor) => (
    <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">{rotulo}</span><span className={`font-mono font-semibold tabular-nums ${cor}`}>{formatCurrency(valor)}</span></div>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <CardResumo label="Quantidade vendida" value={qtd} icon={ShoppingBag} />
      <div className="bg-card rounded-xl border p-4">
        <div className="flex items-center justify-between mb-2"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Faturamento</p><DollarSign className="w-4 h-4 text-emerald-500" /></div>
        <p className="text-2xl font-bold tabular-nums leading-tight">{formatCurrency(fat + servicos)}</p>
        <div className="mt-2 space-y-0.5 text-[11px]">
          {linha("Produtos", fat, "text-emerald-600 dark:text-emerald-400")}
          {linha("Serviços (entregas)", servicos, "text-orange-600 dark:text-orange-400")}
          <div className="pt-1 border-t border-border/60">{linha("Custo de produtos", cmv, "text-red-500")}</div>
        </div>
      </div>
      <div className={`rounded-xl border p-4 ${tomMargem(margem)}`}>
        <div className="flex items-center justify-between mb-2"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Lucro (produtos)</p><TrendingUp className={`w-4 h-4 ${margem >= 30 ? "text-emerald-500" : margem >= 15 ? "text-amber-500" : "text-red-500"}`} /></div>
        <p className={`text-2xl font-bold tabular-nums leading-tight ${textoMargem(margem)}`}>{formatCurrency(lucro)}</p>
        <p className="text-[11px] text-muted-foreground mt-2">Margem média <span className="font-mono font-semibold">{margem.toFixed(1).replace(".", ",")}%</span></p>
        <p className="text-[10px] text-muted-foreground/70 mt-1">Serviços não entram aqui (custos externos)</p>
      </div>
    </div>
  );
}

const carregarProdutos = (periodo) => buscarDetalhado("produtos", periodo);

// Relatórios → Relatório de Produtos. Conteúdo igual ao de antes de 21/09/2026,
// só separado em arquivos.
export default function ProdutosTab({ definirAtualizar }) {
  const p = usePeriodo();
  const { dados, carregando, carregou, recarregar } = useDados(carregarProdutos, p.periodo, { rotulo: "os produtos" });
  const [sort, setSort] = useState({ key: "quantidade", dir: "desc" });
  useEffect(() => { definirAtualizar({ recarregar, carregando }); }, [definirAtualizar, recarregar, carregando]);

  const produtos = dados?.produtos || [];
  const exportar = () => exportarExcel(ordenar(produtos, sort), COLUNAS_EXCEL, `Produtos_${rotuloArquivo(p.periodo)}.xlsx`);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0"><FiltroPeriodo {...p} /></div>
        {produtos.length > 0 && <Button variant="outline" size="sm" className="gap-2 shrink-0" onClick={exportar}><Download className="w-4 h-4" /> Excel</Button>}
      </div>
      {!carregou ? <EsqueletoProdutos /> : (
        <Esmaecer ativo={carregando}>
          {!produtos.length ? <Vazio texto="Nenhum produto vendido no período" /> : (
            <>
              <Kpis produtos={produtos} servicos={dados.total_servicos || 0} />
              <Top10Produtos produtos={produtos} />
              <TabelaProdutos produtos={produtos} sort={sort} setSort={setSort} />
            </>
          )}
        </Esmaecer>
      )}
    </div>
  );
}
